/**
 * Rasterizes a viewBox WINDOW of an SVG document at an exact pixel size
 * (map-FPS perf Stage 3W).
 *
 * Why not `drawImage(img, sx, sy, sw, sh, …)`: the label SVGs are
 * dimensionless (viewBox only, no width/height), and for such images
 * Chrome reports an intrinsic size of 150×150 — a SOURCE-RECT crop is
 * interpreted in that intrinsic space, so any rect expressed in the
 * 0..4096 viewBox space samples garbage (verified: full-image draws are
 * vector-re-rasterized correctly, sub-rect draws are not). Instead we
 * rewrite the ROOT `<svg>` tag with an explicit `width`/`height` equal to
 * the raster target and a `viewBox` equal to the wanted window, load that
 * as a fresh image, and draw it 1:1 — standards-defined, crisp at any
 * zoom, windowing native.
 *
 * Cost: the browser re-parses + re-rasterizes the SVG document once per
 * call — which Stage 3W only does on gesture end (the whole point: once
 * per settle, not once per frame).
 */
import type { LabelRasterPlan } from './labelRasterScheduler'

/**
 * Pure part — the wrapper markup. Replaces the root tag's `viewBox` (and
 * any stray root `width`/`height`) with the window + exact pixel size.
 */
export function buildWindowedSvgMarkup(svgText: string, plan: LabelRasterPlan): string {
  return svgText.replace(/<svg\b[^>]*>/, (rootTag) => {
    const cleaned = rootTag.replace(/\s(?:viewBox|width|height)="[^"]*"/g, '').replace(/>$/, '')
    const { region } = plan
    return (
      `${cleaned} width="${plan.pixelW}" height="${plan.pixelH}"` +
      ` viewBox="${region.x} ${region.y} ${region.w} ${region.h}">`
    )
  })
}

/**
 * Loads the windowed markup as an image ready for a 1:1 `drawImage`.
 * The caller draws it and MUST NOT retain it (the object URL is revoked
 * once the image has decoded).
 */
export async function rasterizeSvgWindow(
  svgText: string,
  plan: LabelRasterPlan,
): Promise<HTMLImageElement> {
  const markup = buildWindowedSvgMarkup(svgText, plan)
  const url = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml' }))
  const image = new Image()
  image.src = url
  try {
    await image.decode()
  } finally {
    URL.revokeObjectURL(url)
  }
  return image
}
